/* globals: SystemJS */
// Ensure that config is the first import
import './config';
import { QUERY_PATTERN, Constants } from './constants';
import { registerApplication, start as startSingleSpa } from 'single-spa';
import { broadcastCurrentUser, configure as configureAuth0 } from '@sage/sbc.core.authentication.ui';
import { cleanup as cleanupScene, configure as configureScene } from '@sage/sbc.core.scene.ui';
import { createBrowserHistory } from 'history';
import { createConfig, createLogger } from '@sage/sbc.core.utils.ui';
import { ServiceWorker } from './service-workers/service-worker';
import { deleteIndexDbKeys } from './utils/deleteIndexDbKeys';
import { getUrlParams } from './utils/url-helper';

const { config } = createConfig();
const history = createBrowserHistory();
const logger = createLogger();
const pathname = (str) => (location) => location.pathname.includes(str);
const startsWithPathname = (str: string) => (location: Location) => location.pathname.startsWith(str);
const rootpath = () => (location) => location.pathname === '/';

const props = {
  history,
  domElementGetter: () => document.getElementById('app')
};

let previousRootState = null;

const unlisten = history.listen((listen) => {
  if (listen.location.pathname === '/' && listen.action === 'POP') {
    const previousBid = previousRootState && getUrlParams(previousRootState.location.search.replace('?', ''), 'bid');
    const currentBid = getUrlParams(listen.location.search.replace('?', ''), 'bid');

    const aid = getUrlParams(listen.location.search.replace('?', ''), 'aid');

    // AID is included when business is switched. This is just to ensure we do not reload the page for other scenarios
    if (currentBid && aid && currentBid !== previousBid) {
      console.log('Force refresh for business change');
      unlisten();
      window.location.reload();
    }
    previousRootState = listen;
  }
});

const supportedLocales = config('supported_locales');
const clearUserDataCallback = async () => {
  if (!supportedLocales.some((locale) => window.location.pathname.includes(locale))) {
    deleteIndexDbKeys(QUERY_PATTERN);
    // Clear local storage, excluding import map overrides where applicable
    const preserve = {};
    if (config('features.sbc.core.devtools.ui.enabled')) {
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('import-map-override') ||
          [
            Constants.GLOBAL_CONFIG_OVERRIDES,
            Constants.GLOBAL_HANDLER_DEFINITIONS,
            Constants.GLOBAL_PERMISSIONS_OVERRIDES,
            Constants.GLOBAL_FEATURES_OVERRIDES,
            Constants.GLOBAL_MOCK_WORKFLOW_NOTIFICATIONS,
            Constants.PAYROLL_QUERYPARAMS,
            Constants.PAYROLL_SHOW_ONBOARDING_HELP,
          ].includes(key)
        ) {
          preserve[key] = localStorage.getItem(key);
        }
      });
    }

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(Constants.PAYROLL_MODALPARAMS)) {
        preserve[key] = localStorage.getItem(key);
      }
    });

    localStorage.clear();

    Object.keys(preserve).forEach((key) => {
      localStorage.setItem(key, preserve[key]);
    });

    // Clear session storage
    const sessionPreserve = {};
    Object.keys(sessionStorage).forEach((key) => {
      if (
        [
          Constants.SESSION_USER_STATE,
          Constants.SESSION_EMAIL_ADDRESS,
          Constants.SESSION_ENVIRONMENT,
          Constants.PAYROLL_QUERYPARAMS
        ].includes(key)
      ) {
        sessionPreserve[key] = sessionStorage.getItem(key);
      }
    });

    sessionStorage.clear();
    Object.keys(sessionPreserve).forEach((key) => {
      sessionStorage.setItem(key, sessionPreserve[key]);
    });

    // Clear service worker cache
    const response = new Promise<void>((resolve) => {
      if (navigator.serviceWorker) {
        navigator.serviceWorker.onmessage = (event) => {
          if (event.data) {
            const message = JSON.parse(event.data);
            if (message.type === 'CACHE_INVALIDATED') {
              resolve();
            }
          }
        };
      }
    });

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        await navigator.serviceWorker.ready.then((registration) => {
          registration.active.postMessage('INVALIDATE_CACHE');
        });

        await response;
      } catch (error) {
        logger.error(error);
      }
    }
    cleanupScene();
  }
};

window.addEventListener('storage', (event) => {
  if (event.storageArea !== localStorage) {
    return;
  }

  if (event.key === 'clearUserData') {
    (async () => {
      await clearUserDataCallback();
      if (!pathname('/logout')(location) && !pathname('/logged-out')(location)) {
        window.location.reload();
      }
    })();
  }
});

window.addEventListener('single-spa:before-routing-event', (event) => {
  const { oldUrl, newUrl } = (event as CustomEvent).detail;

  const urlsToHideNavBar = ['/desktop/copilot', '/cloudid/callback', '/vscode/copilot'];
  const navigationBar = document.getElementById('nav');

  if (!navigationBar) {
    return;
  }

  /**
   * if new url and old url do not include one of the values in urlsToHideNavBar then return null
   */
  if (!urlsToHideNavBar.some((url) => newUrl.includes(url)) && !urlsToHideNavBar.some((url) => oldUrl.includes(url))) {
    return null;
  }

  if (urlsToHideNavBar.some((url) => newUrl.includes(url))) {
    navigationBar.style.display = 'none';
  } else {
    navigationBar.style.display = '';
    if (!navigationBar.classList.contains('nav-styling')) {
      navigationBar.classList.add('nav-styling');
    }
  }
});

configureAuth0({
  ...config('cloudid'),
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
  clearUserDataCallback
});

configureScene(history);

const activityFns = {
  welcome: rootpath(),
  notifications: pathname('/notifications'),
  'global-pages': pathname('/error'),
  'client-management': pathname('/client-management'),
  manageProfile: pathname('/settings/profile-management'),
  manageBusiness: pathname('/settings/business-management'),
  'manage-business-secondary-nav': pathname('/settings/business-management'),
  manageUser: pathname('/settings/user-management'),
  'user-management-secondary-nav': pathname('/settings/user-management'),
  invitations: pathname('/invitations'),
  paymentPageCallback: pathname('/payment-page/callback'),
  callback: pathname('/cloudid/callback'),
  logout: pathname('/logout'),
  'logged-out': pathname('/logged-out'),
  home: pathname('/home'),
  'partnership-tax': pathname('/partnership-tax/'),
  'entity-list': pathname('/client-management/work-view'),
  'desktop-copilot': pathname('/desktop/copilot'),
  'account-management': pathname('/settings/account-management')
} as Record<string, (location: Location) => boolean>;

try {
  if (!activityFns.callback(window.location)) {
    // When in a CloudID callback, the CloudID callback page will initialise the current user
    // When not on a CloudID callback, we need to initialise the current user
    broadcastCurrentUser();
  }

  const shouldShowSimpleNavBar = (location: Location) =>
    rootpath()(location) ||
    activityFns['global-pages'](location) ||
    activityFns['logout'](location) ||
    activityFns['logged-out'](location);

  registerApplication(
    'sbc.core.globalnav.ui-simplenavbar',
    () => System.import('sbc.core.globalnav.ui/index.js').then(({ SimpleNavBarLifecycles }) => SimpleNavBarLifecycles),
    shouldShowSimpleNavBar,
    {
      ...props,
      domElementGetter: () => document.getElementById('nav')
    }
  );

  const shouldShowGlobalNav = (location: Location) =>
    !shouldShowSimpleNavBar(location) &&
    !pathname('/payment-page/callback')(location) &&
    !pathname('/accounts/invoicing/preview')(location) &&
    !pathname('/accounts/invoicing/payment')(location) &&
    !pathname('/desktop/copilot')(location) &&
    !pathname('/generation_experts')(location) &&
    !pathname('/cloudid/callback')(location) &&
    !pathname('/vscode/copilot')(location);

  registerApplication(
    'sbc.core.globalnav.ui',
    () => System.import('sbc.core.globalnav.ui/index.js').then(({ NavBarLifecycles }) => NavBarLifecycles),
    (location) => !pathname('/accounting/quickstart')(location) && shouldShowGlobalNav(location),
    {
      ...props,
      domElementGetter: () => document.getElementById('nav')
    }
  );

  registerApplication(
    'sbc.core.globalnav.ui-callback',
    () =>
      System.import('sbc.core.globalnav.ui/index.js').then(
        ({ CloudIDCallbackLifecycles }) => CloudIDCallbackLifecycles
      ),
    activityFns['callback'],
    props
  );

  registerApplication(
    'sbc.core.globalnav.ui-logout',
    () => System.import('sbc.core.globalnav.ui/index.js').then(({ LogoutPageLifecycles }) => LogoutPageLifecycles),
    activityFns['logout'],
    props
  );

  registerApplication(
    'sbc.core.globalnav.ui-logged-out',
    () =>
      System.import('sbc.core.globalnav.ui/index.js').then(({ LoggedOutPageLifecycles }) => LoggedOutPageLifecycles),
    activityFns['logged-out'],
    props
  );

  registerApplication(
    'sbc.utility.notification.ui',
    () =>
      System.import('sbc.utility.notification.ui/index.js').then(
        ({ NotificationsPageLifecycles }) => NotificationsPageLifecycles
      ),
    activityFns['notifications'],
    props
  );

  const shouldLoadConsent = (location) =>
    /**
     * This is blocked as it is an callback in an iFrame
     */
    !pathname('/payment-page/callback')(location) &&
    /**
     * When we are on the logout page we do not want to be attempting to pop the consent modal
     */
    !pathname('/logout')(location) &&
    /**
     * When we are on the logged out page we do not want to be attempting to pop the consent modal
     */
    !pathname('/logged-out')(location) &&
    /**
     * When we are on the cloud id page we do not want to be attempting to pop the consent modal
     */
    !pathname('/cloudid/callback')(location) &&
    /**
     * When we are on the cloud id callback page we do not want to be attempting to pop the consent modal
     */
    !pathname('/sso-login-completion')(location) &&
    /**
     * When we are on the client selection page we do not want to be attempting to pop the consent modal
     */
    !pathname('/client-selection')(location) &&
    /**
     * We do not want to load consent until the url is one of the products
     */
    !rootpath()(location);

  registerApplication(
    'sbc.core.consent.ui',

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    () => System.import('sbc.core.consent.ui/index.js'),
    shouldLoadConsent,
    { history }
  );

  registerApplication(
    'global_payroll/auth',

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    () => System.import('global_payroll/auth'),
    (location) =>
      location.pathname.startsWith('/') &&
      !location.pathname.startsWith('/cloudid/callback') &&
      !location.pathname.startsWith('/logout') &&
      !supportedLocales.some((locale) => location.pathname.startsWith(`/${locale}`)),
    {
      ...props,
      domElementGetter: () => document.getElementById('app')
    }
  );

  supportedLocales.forEach((locale) => {
    registerApplication(
      `global_payroll/${locale}`,

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      () => System.import(`global_payroll/${locale}`),
      (location) => location.pathname.startsWith(`/${locale}`),
      {
        ...props,
        domElementGetter: () => document.getElementById('app')
      }
    );
  });

  const shouldLoadDatalayer = (location: Location) =>
    /**
     * This is blocked as it is an callback in an iFrame
     */
    !startsWithPathname('/payment-page/callback')(location) &&
    /**
     * When we are on the logout page so won't have access to scene
     */
    !startsWithPathname('/logout')(location) &&
    /**
     * When we are on the logged out page so won't have access to scene
     */
    !startsWithPathname('/logged-out')(location) &&
    /**
     * When we are on the cloud id page so won't have access to scene
     */
    !startsWithPathname('/cloudid/callback')(location) &&
    /**
     * When we are on the invitations page you are logged out so won't have access to scene
     */
    !startsWithPathname('/invitations')(location) &&
    /**
     * When we are on the shared invoice public page so won't have access to scene
     */
    !startsWithPathname('/accounts/invoicing/preview')(location) &&
    /**
     * When we are on the pay now public page so won't have access to scene
     */
    !startsWithPathname('/accounts/invoicing/payment')(location) &&
    /**
     * When we are on the error route and can't assume that scene is available
     */
    !startsWithPathname('/error')(location) &&
    /**
     * When completing callback in payroll, do not load datalayer
     */
    !pathname('/sso-login-completion')(location) &&
    /**
     * Calling scene here results in a 404 from orgService when attempting a redirect after login with a platform query param
     */
    !rootpath()(location);

  registerApplication(
    'datalayer',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    () => System.import('sbc.core.datalayer.ui/index.js'),
    shouldLoadDatalayer,
    { history }
  );

  ServiceWorker.register().then(() => {
    startSingleSpa();
  });
} catch (error) {
  logger.error(error);
  history.push('/error');
}
